import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, Minus, Plus } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatINR } from "@/lib/api";
import { useBranding } from "@/context/BrandingContext";

const CartPage = () => {
  const { items, updateQty, removeItem, subtotal } = useCart();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const threshold = branding.free_shipping_threshold || 1499;
  const remainder = Math.max(0, threshold - subtotal);

  if (items.length === 0) {
    return (
      <div className="rieave-container py-24 text-center" data-testid="cart-page-empty">
        <p className="eyebrow mb-3">Your bag</p>
        <h1 className="font-hero text-5xl mb-5">Your bag is <span className="serif-italic text-rose">waiting.</span></h1>
        <p className="text-mauve mb-8">Start browsing our editorial picks — each piece is a story waiting to be worn.</p>
        <Link to="/shop" className="btn-primary">Browse collection →</Link>
      </div>
    );
  }

  return (
    <div className="rieave-container py-12 md:py-16" data-testid="cart-page">
      <p className="eyebrow mb-2">Bag</p>
      <h1 className="font-hero text-4xl md:text-5xl mb-8">Your curated <span className="serif-italic text-rose">selection.</span></h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-5">
          {items.map(it => (
            <div key={`${it.product_id}-${it.size || "_"}`} className="flex gap-5 p-5 bg-white rounded-2xl border border-border" data-testid={`cart-row-${it.product_id}`}>
              <Link to={`/product/${it.slug}`} className="w-24 h-32 rounded-xl overflow-hidden bg-blush/40 flex-shrink-0">
                {it.image && <img src={it.image} alt={it.name} className="w-full h-full object-cover" />}
              </Link>
              <div className="flex-1 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <Link to={`/product/${it.slug}`} className="font-cormorant text-xl hover:text-rose">{it.name}</Link>
                  {it.size && <p className="font-label text-[11px] uppercase tracking-[0.22em] text-mauve mt-1">Size {it.size}</p>}
                  <p className="font-body text-sm mt-2">{formatINR(it.price)}</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="inline-flex items-center border border-border rounded-full">
                    <button className="p-2" onClick={() => updateQty(it.product_id, it.size, it.quantity - 1)} aria-label="minus"><Minus size={14} /></button>
                    <span className="w-7 text-center font-label text-sm">{it.quantity}</span>
                    <button className="p-2" onClick={() => updateQty(it.product_id, it.size, it.quantity + 1)} aria-label="plus"><Plus size={14} /></button>
                  </div>
                  <p className="font-cormorant text-lg w-24 text-right">{formatINR(it.price * it.quantity)}</p>
                  <button className="text-mauve hover:text-rose" onClick={() => removeItem(it.product_id, it.size)} aria-label="remove"><Trash2 size={16} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <aside className="bg-white rounded-2xl border border-border p-6 h-fit lg:sticky lg:top-24" data-testid="cart-summary">
          <p className="eyebrow mb-3">Summary</p>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span>{formatINR(subtotal)}</span></div>
            <div className="flex justify-between"><span>Shipping</span><span>{subtotal >= threshold ? "Free" : formatINR(99)}</span></div>
            {remainder > 0 && <p className="text-xs text-mauve">Spend {formatINR(remainder)} more for free shipping.</p>}
          </div>
          <hr className="my-4 border-border" />
          <div className="flex justify-between items-baseline mb-5">
            <span className="font-label text-sm uppercase tracking-[0.22em]">Total</span>
            <span className="font-hero text-3xl">{formatINR(subtotal + (subtotal >= threshold ? 0 : 99))}</span>
          </div>
          <button className="btn-primary w-full" onClick={() => navigate("/checkout")} data-testid="cart-proceed-checkout">Proceed to checkout →</button>
          <Link to="/shop" className="btn-link w-full justify-center mt-3">Continue shopping</Link>
        </aside>
      </div>
    </div>
  );
};

export default CartPage;
